/**
 * TS-DRS: Extra Free Termék Automatikus Hozzáadása
 *
 * Ha egy terméknél be van kapcsolva az "extra free" metafield,
 * automatikusan hozzáadja a kosárhoz a megadott üvegbetét díj terméket.
 */

(function() {
  'use strict';

  // Konfiguráció - Itt adhatod meg az üvegbetét díj termék ID-ját
  const CONFIG = {
    extraProductVariantId: null, // Ezt be kell állítani a termék variant ID-val vagy product ID-val
    extraProductId: null, // Product ID (ha variant ID helyett product ID-t adsz meg)
    extraProductHandle: null, // Product handle (opcionális, ha product ID-t használsz)
    metaFieldKey: 'custom.extra_fee', // A metafield kulcs
    metaFieldValue: true // A metafield értéke, amit figyelünk
  };

  // Debounce funkció a gyakori hívások elkerülésére
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Kosár lekérése
  async function getCart() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      if (!response.ok) throw new Error('Cart fetch failed');
      return await response.json();
    } catch (error) {
      console.error('TS-DRS: Error fetching cart', error);
      return null;
    }
  }

  // Termék adatok lekérése (metafield-ekkel együtt)
  async function getProductData(productId, productHandle = null) {
    try {
      let handle = productHandle;

      // Ha nincs handle, megkeressük a kosárból
      if (!handle) {
        const cart = await getCart();
        if (!cart) {
          return null;
        }

        const cartItem = cart.items.find(item => item.product_id === productId);
        if (!cartItem) {
          return null;
        }
        if (!cartItem.handle) {
          return null;
        }

        handle = cartItem.handle;
      }

      // A termék handle-ját használjuk a JSON API-hoz
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error('Product fetch failed');
      }
      const productData = await response.json();

      return productData;
    } catch (error) {
      console.error('TS-DRS: Error fetching product data', error);
      return null;
    }
  }

  // Termék metafield értékének ellenőrzése
  function hasExtraFreeMetafield(product) {
    if (!product) {
      return false;
    }

    // Különböző Shopify API formátumok kezelése
    let metafields = null;

    if (product.metafields && Array.isArray(product.metafields)) {
      metafields = product.metafields;
    } else if (product.metafields_global) {
      // Ha van globális metafields objektum
      metafields = Object.values(product.metafields_global).flat();
    } else if (product.metafields && typeof product.metafields === 'object') {
      // Ha objektum formátumban van
      metafields = Object.values(product.metafields);
    }

    if (!metafields || !Array.isArray(metafields)) {
      return false;
    }

    // Ellenőrizzük a custom.extra_free metafield-et
    const extraFree = metafields.find(m => {
      if (!m) return false;

      // Különböző kulcs formátumok kezelése
      const keyMatch = m.key === 'extra_free' || m.key === 'custom.extra_free' || m.key === 'dr';
      const namespaceMatch = m.namespace === 'custom' || !m.namespace;

      return keyMatch && namespaceMatch;
    });

    if (!extraFree) {
      return false;
    }

    // Érték ellenőrzése különböző formátumokra
    const value = extraFree.value;
    const isValid = value === true || value === 'true' || value === '1' || value === 1;

    return isValid;
  }

  // Termék adatok cache
  const productCache = new Map();

  // Üvegbetét termék variant ID cache
  let cachedExtraProductVariantId = null;

  // Product ID-ból variant ID lekérése
  async function getVariantIdFromProductId(productId, productHandle = null) {
    try {
      // Ha a productId egy GID, kinyerjük a számot
      let cleanProductId = productId;
      const gidMatch = String(productId).match(/Product\/(\d+)/);
      if (gidMatch) {
        cleanProductId = gidMatch[1];
      }

      let handle = productHandle || CONFIG.extraProductHandle;

      if (!handle) {
        console.error('TS-DRS: Product handle szükséges Product ID használatához. A handle a termék URL-jében található (pl: /products/uvegbetet-dij -> handle: uvegbetet-dij)');
        return null;
      }

      // Lekérjük a termék adatokat
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product fetch failed');
      const productData = await response.json();

      // Megkeressük az első elérhető variant ID-t
      if (productData.variants && productData.variants.length > 0) {
        // Először próbáljuk az elérhető variant-ot
        const availableVariant = productData.variants.find(v => v.available);
        if (availableVariant) {
          return availableVariant.id;
        }
        // Ha nincs elérhető, az első variant ID-t használjuk
        return productData.variants[0].id;
      }

      return null;
    } catch (error) {
      console.error('TS-DRS: Error getting variant ID from product ID', error);
      return null;
    }
  }

  // Üvegbetét termék variant ID-jának megszerzése
  async function getExtraProductVariantId() {
    // Ha már van cache-ben, használjuk azt
    if (cachedExtraProductVariantId) {
      return cachedExtraProductVariantId;
    }

    // Ha van variant ID beállítva, használjuk azt
    if (CONFIG.extraProductVariantId) {
      cachedExtraProductVariantId = CONFIG.extraProductVariantId;
      return cachedExtraProductVariantId;
    }

    // Ha van product ID beállítva, próbáljuk meg variant ID-t kinyerni
    if (CONFIG.extraProductId) {
      const variantId = await getVariantIdFromProductId(CONFIG.extraProductId, CONFIG.extraProductHandle);
      if (variantId) {
        cachedExtraProductVariantId = variantId;
        return variantId;
      }
    }

    return null;
  }

  // Termék oldalról extra fee ellenőrzése (data attribute alapján)
  async function checkExtraFreeFromProductPage(productHandle) {
    try {
      // Lekérjük a termék oldal HTML-jét
      const response = await fetch(`/products/${productHandle}`);
      if (!response.ok) {
        return false;
      }

      const html = await response.text();

      // Ellenőrizzük, hogy van-e data-extra-fee a HTML-ben
      if (html.includes('data-extra-fee')) {
        // Keresünk a HTML string-ben
        const extraFeeMatch = html.match(/data-extra-fee=["']([^"']+)["']/i);
        if (extraFeeMatch) {
          const extraFeeValue = extraFeeMatch[1];
          return extraFeeValue === 'true';
        }
      }

      // Ha regex nem talált, próbáljuk a DOMParser-t
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Keresünk product-form elemet data-extra-fee attribútummal
      const productForm = doc.querySelector('product-form[data-extra-fee]');
      if (productForm) {
        const extraFee = productForm.getAttribute('data-extra-fee');
        return extraFee === 'true';
      }

      // Ha nincs product-form, keresünk form elemet
      const form = doc.querySelector('form[data-extra-fee]');
      if (form) {
        const extraFee = form.getAttribute('data-extra-fee');
        return extraFee === 'true';
      }

      // Keresünk bármilyen elemet data-extra-fee attribútummal
      const anyElement = doc.querySelector('[data-extra-fee]');
      if (anyElement) {
        const extraFee = anyElement.getAttribute('data-extra-fee');
        return extraFee === 'true';
      }

      return false;
    } catch (error) {
      console.error('TS-DRS: Hiba a termék oldal lekérése során', error);
      return false;
    }
  }

  // Üvegbetét termék mennyiségének kiszámítása
  async function calculateExtraProductQuantity(cart) {
    if (!cart || !cart.items) {
      return 0;
    }

    const extraVariantId = await getExtraProductVariantId();

    if (!extraVariantId) {
      return 0;
    }

    let totalQuantity = 0;

    // Végigmegyünk a kosár item-eken
    for (const item of cart.items) {
      // Ha ez az üvegbetét termék, kihagyjuk
      if (item.variant_id === extraVariantId) {
        continue;
      }

      // Ellenőrizzük, hogy van-e extra fee metafield
      // Először ellenőrizzük a kosár item properties-t (ha van)
      let hasExtraFee = false;

      if (item.properties) {
        // A Shopify kosár API-ban a properties egy objektum vagy tömb lehet
        if (Array.isArray(item.properties)) {
          const extraFeeProperty = item.properties.find(p => {
            if (typeof p === 'string') {
              return p === '_extra_fee' || p.includes('_extra_fee');
            }
            if (typeof p === 'object' && p !== null) {
              return p.name === '_extra_fee' || p.key === '_extra_fee';
            }
            return false;
          });
          if (extraFeeProperty) {
            hasExtraFee = true;
          }
        } else if (typeof item.properties === 'object' && item.properties !== null) {
          // Ha objektum, keresünk benne
          if (item.properties._extra_fee === 'true' || item.properties._extra_fee === true) {
            hasExtraFee = true;
          }
          // Végigmegyünk az objektum kulcsain
          for (const key in item.properties) {
            if (key === '_extra_fee' || key.includes('_extra_fee')) {
              hasExtraFee = true;
              break;
            }
          }
        }
      }

      // Ha nincs property, próbáljuk a cache-ből
      if (!hasExtraFee) {
        hasExtraFee = productCache.get(`extra_fee_${item.product_id}`);
      }

      if (hasExtraFee === undefined || hasExtraFee === false) {
        if (item.handle) {
          // Ha nincs cache-ben, lekérjük a termék oldalt és ellenőrizzük a data attribute-ot
          hasExtraFee = await checkExtraFreeFromProductPage(item.handle);
          // Cache-eljük az eredményt
          productCache.set(`extra_fee_${item.product_id}`, hasExtraFee);
        }
      }

      if (hasExtraFee) {
        totalQuantity += item.quantity;
      }
    }

    return totalQuantity;
  }

  // Üvegbetét termék hozzáadása/frissítése a kosárhoz
  async function syncExtraProduct() {
    const extraVariantId = await getExtraProductVariantId();

    if (!extraVariantId) {
      console.warn('TS-DRS: extraProductVariantId vagy extraProductId nincs beállítva');
      return;
    }

    const cart = await getCart();

    if (!cart) {
      return;
    }

    // Megkeressük az üvegbetét terméket a kosárban
    const extraProductItem = cart.items.find(item => item.variant_id === extraVariantId);

    // Kiszámoljuk a szükséges mennyiséget
    const requiredQuantity = await calculateExtraProductQuantity(cart);

    if (requiredQuantity === 0) {
      // Ha nincs szükség rá, eltávolítjuk
      if (extraProductItem) {
        await updateCartItemQuantity(extraProductItem.key, 0);
      }
      return;
    }

    // Ha van már a kosárban, frissítjük a mennyiséget
    if (extraProductItem) {
      if (extraProductItem.quantity !== requiredQuantity) {
        await updateCartItemQuantity(extraProductItem.key, requiredQuantity);
      }
    } else {
      // Ha nincs, hozzáadjuk
      await addToCart(extraVariantId, requiredQuantity);
    }
  }

  // Termék hozzáadása a kosárhoz
  async function addToCart(variantId, quantity = 1) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      // Ne használjunk sections paramétert, mert a Shopify API rossz HTML-t ad vissza
      // A drawer-t külön frissítjük a hozzáadás után

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        const errorText = await response.text();
        console.error('TS-DRS: Add to cart failed:', response.status, errorText);
        throw new Error('Add to cart failed');
      }

      // Ellenőrizzük, hogy JSON-e a válasz
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Ha HTML válasz jön, akkor a kosár valószínűleg frissült
        // Lekérjük a kosár állapotát, hogy megerősítsük a sikeres hozzáadást
        const cart = await getCart();
        if (cart) {
          data = { items: cart.items, item_count: cart.item_count };
        } else {
          data = { success: true };
        }
      }

      // Frissítsük a drawer-t külön, ha van
      // Ne használjuk az onCartUpdate() metódust, mert az a main-cart-items section-t kéri le
      // Várunk egy kicsit, hogy a product-form.js befejezze a frissítést
      await new Promise(resolve => setTimeout(resolve, 300));

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawerItems && cartDrawer) {
        // Ellenőrizzük, hogy nincs-e már rossz HTML a drawer-ben (product-form.js által frissített)
        const currentInnerHTML = cartDrawerItems.innerHTML;
        const hasBadHTML = currentInnerHTML.includes('page-header') || currentInnerHTML.includes('cart__warnings') || currentInnerHTML.includes('<table class="cart-items">');

        // Mindig frissítsük a drawer-t, hogy biztosak legyünk, hogy helyes HTML van benne
        try {
          const drawerResponse = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
          const drawerHTML = await drawerResponse.text();
          const parser = new DOMParser();
          const drawerDoc = parser.parseFromString(drawerHTML, 'text/html');

          // A Shopify section wrapper div-ben jön vissza, vagy közvetlenül a cart-drawer
          const sectionWrapper = drawerDoc.querySelector('#shopify-section-cart-drawer') || drawerDoc.querySelector('[id*="shopify-section"]');
          const newDrawer = sectionWrapper ? sectionWrapper.querySelector('cart-drawer') : drawerDoc.querySelector('cart-drawer');

          if (newDrawer) {
            // Kérjük le az új cart-drawer-items elemet
            const newDrawerItems = newDrawer.querySelector('cart-drawer-items');
            if (newDrawerItems && cartDrawerItems) {
              // Ellenőrizzük, hogy nincs-e benne cart oldal HTML (page-header, cart__warnings, table stb.)
              const newInnerHTML = newDrawerItems.innerHTML;
              if (newInnerHTML.includes('page-header') || newInnerHTML.includes('cart__warnings') || newInnerHTML.includes('<table class="cart-items">')) {
                // Ha még mindig rossz HTML jön vissza, ne frissítsük
                return data;
              }

              // Frissítsük csak a cart-drawer-items tartalmát
              cartDrawerItems.innerHTML = newInnerHTML;
            }
          }
        } catch (drawerError) {
          console.error('TS-DRS: Drawer frissítés hiba:', drawerError);
        }
      }

      // Publikáljuk a cartUpdate eseményt, ha elérhető
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'ts-drs', variantId: variantId});
      }

      return data;
    } catch (error) {
      console.error('TS-DRS: Error adding to cart', error);
      return null;
    }
  }

  // Kosár item mennyiségének frissítése
  async function updateCartItemQuantity(itemKey, quantity) {
    try {
      // Próbáljuk meg használni a cart-drawer-items updateQuantity metódusát, ha van
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.updateQuantity === 'function') {
        // Kérjük le a kosár állapotát, hogy megkeressük a line számot
        const cart = await getCart();
        if (cart && cart.items) {
          const itemIndex = cart.items.findIndex(item => item.key === itemKey || item.id === itemKey);
          if (itemIndex !== -1) {
            const line = itemIndex + 1; // 1-based index
            // A cart-drawer-items updateQuantity metódusa helyesen kezeli a drawer frissítését
            // Ez aszinkron módon fut, de nem ad vissza promise-t, ezért várunk egy kicsit
            cartDrawerItems.updateQuantity(line, quantity, 'quantity');
            // Várunk egy kicsit, hogy a metódus befejeződjön
            await new Promise(resolve => setTimeout(resolve, 500));
            return { success: true };
          }
        }
      }

      // Ha nincs cart-drawer-items, használjuk a cart-items updateQuantity metódusát
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        const cart = await getCart();
        if (cart && cart.items) {
          const itemIndex = cart.items.findIndex(item => item.key === itemKey || item.id === itemKey);
          if (itemIndex !== -1) {
            const line = itemIndex + 1;
            cartItems.updateQuantity(line, quantity, 'quantity');
            await new Promise(resolve => setTimeout(resolve, 500));
            return { success: true };
          }
        }
      }

      // Ha nincs egyik sem, használjuk a normál API-t SECTIONS NÉLKÜL
      // Ne használjunk sections paramétert, mert a Shopify API rossz section-t ad vissza

      // Kérjük le a kosár állapotát, hogy megkeressük a line számot
      const cartData = await getCart();
      if (!cartData || !cartData.items) {
        console.error('TS-DRS: Nem sikerült lekérni a kosár állapotát');
        return null;
      }

      const itemIndex = cartData.items.findIndex(item => item.key === itemKey || item.id === itemKey);
      if (itemIndex === -1) {
        console.error('TS-DRS: Item nem található a kosárban:', itemKey);
        return null;
      }

      const line = itemIndex + 1; // 1-based index

      // Ne használjunk sections paramétert!
      const body = JSON.stringify({
        line: line,
        quantity: quantity
      });

      // Használjuk a fetchConfig-ot, mint a cart.js-ben
      const fetchConfigOptions = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfigOptions,
        body: body
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('TS-DRS: Cart update failed:', response.status, errorText);
        throw new Error('Cart update failed');
      }

      // A válasz csak a kosár állapotát tartalmazza, nem section-öket
      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);

        // Most frissítsük a drawer-t külön, ha van
        // Ne használjuk az onCartUpdate() metódust, mert az a main-cart-items section-t kéri le
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawerItems && cartDrawer) {
          try {
            const drawerResponse = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
            const drawerHTML = await drawerResponse.text();
            const parser = new DOMParser();
            const drawerDoc = parser.parseFromString(drawerHTML, 'text/html');

            // A Shopify section wrapper div-ben jön vissza, vagy közvetlenül a cart-drawer
            const sectionWrapper = drawerDoc.querySelector('#shopify-section-cart-drawer') || drawerDoc.querySelector('[id*="shopify-section"]');
            const newDrawer = sectionWrapper ? sectionWrapper.querySelector('cart-drawer') : drawerDoc.querySelector('cart-drawer');

            if (newDrawer) {
              // Kérjük le az új cart-drawer-items elemet
              const newDrawerItems = newDrawer.querySelector('cart-drawer-items');
              if (newDrawerItems && cartDrawerItems) {
                // Ellenőrizzük, hogy nincs-e benne cart oldal HTML (page-header, cart__warnings, table stb.)
                const newInnerHTML = newDrawerItems.innerHTML;
                if (newInnerHTML.includes('page-header') || newInnerHTML.includes('cart__warnings') || newInnerHTML.includes('<table class="cart-items">')) {
                  // Ha tartalmazza a cart oldal HTML-jét, ne frissítsük
                  return data;
                }

                // Frissítsük csak a cart-drawer-items tartalmát
                cartDrawerItems.innerHTML = newInnerHTML;
              }
            }
          } catch (drawerError) {
            console.error('TS-DRS: Drawer frissítés hiba:', drawerError);
          }
        }
      } catch (parseError) {
        console.error('TS-DRS: Response parse hiba:', parseError);
        data = { success: true };
      }

      // Publikáljuk a cartUpdate eseményt, ha elérhető
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'ts-drs', itemKey: itemKey});
      }

      return data;
    } catch (error) {
      console.error('TS-DRS: Error updating cart', error);
      return null;
    }
  }

  // Flag a végtelen loop elkerülésére
  let isSyncing = false;

  // Fő szinkronizáló funkció
  const syncExtraProductDebounced = debounce(async () => {
    // Ha már szinkronizálunk, ne kezdjük újra
    if (isSyncing) {
      return;
    }

    isSyncing = true;

    try {
      // Szinkronizáljuk az üvegbetét terméket
      await syncExtraProduct();
    } catch (error) {
      console.error('TS-DRS: Hiba a szinkronizálás során:', error);
    } finally {
      // Kis késleltetés után engedélyezzük újra a szinkronizálást
      setTimeout(() => {
        isSyncing = false;
      }, 1000);
    }
  }, 500);

  // Inicializálás
  function init() {
    // Konfiguráció ellenőrzése
    if (!CONFIG.extraProductVariantId && !CONFIG.extraProductId) {
      console.warn('TS-DRS: Az extraProductVariantId vagy extraProductId nincs beállítva. Kérlek add hozzá a konfigurációhoz!');
      return;
    }

    // Kosár változások figyelése
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // PubSub esemény figyelése
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Ne figyeljük a saját általunk generált eseményeket
        if (event && event.source === 'ts-drs') {
          return;
        }

        // Kis késleltetés, hogy a kosár frissüljön
        setTimeout(() => {
          syncExtraProductDebounced();
        }, 300);
      });
    }

    // DOM ready esemény
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(syncExtraProductDebounced, 1000);
        setupDrawerWatcher();
      });
    } else {
      setTimeout(syncExtraProductDebounced, 1000);
      setupDrawerWatcher();
    }

    // MutationObserver a drawer rossz HTML ellenőrzéséhez
    function setupDrawerWatcher() {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (!cartDrawerItems) {
        // Ha még nincs drawer, próbáljuk meg újra később
        setTimeout(setupDrawerWatcher, 500);
        return;
      }

      let isFixing = false;

      const observer = new MutationObserver((mutations) => {
        // Ha már javítunk, ne kezdjük újra
        if (isFixing) return;

        const currentInnerHTML = cartDrawerItems.innerHTML;
        if (currentInnerHTML.includes('page-header') || currentInnerHTML.includes('cart__warnings') || currentInnerHTML.includes('<table class="cart-items">')) {
          isFixing = true;

          // Késleltetett módon javítjuk, hogy ne legyen végtelen loop
          setTimeout(async () => {
            try {
              const drawerResponse = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
              const drawerHTML = await drawerResponse.text();
              const parser = new DOMParser();
              const drawerDoc = parser.parseFromString(drawerHTML, 'text/html');
              const sectionWrapper = drawerDoc.querySelector('#shopify-section-cart-drawer') || drawerDoc.querySelector('[id*="shopify-section"]');
              const newDrawer = sectionWrapper ? sectionWrapper.querySelector('cart-drawer') : drawerDoc.querySelector('cart-drawer');

              if (newDrawer) {
                const newDrawerItems = newDrawer.querySelector('cart-drawer-items');
                if (newDrawerItems && cartDrawerItems) {
                  const newInnerHTML = newDrawerItems.innerHTML;
                  if (!newInnerHTML.includes('page-header') && !newInnerHTML.includes('cart__warnings') && !newInnerHTML.includes('<table class="cart-items">')) {
                    cartDrawerItems.innerHTML = newInnerHTML;
                  }
                }
              }
            } catch (error) {
              console.error('TS-DRS: Drawer javítás hiba:', error);
            } finally {
              isFixing = false;
            }
          }, 100);
        }
      });

      observer.observe(cartDrawerItems, {
        childList: true,
        subtree: true,
        characterData: false
      });
    }

    // MutationObserver a kosár változások figyelésére
    const cartObserver = new MutationObserver(() => {
      syncExtraProductDebounced();
    });

    // Figyeljük a cart-drawer és cart-items változásait
    const cartDrawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');

    if (cartDrawer) {
      cartObserver.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }

    if (cartItems) {
      cartObserver.observe(cartItems, {
        childList: true,
        subtree: true
      });
    }

    // Custom esemény figyelés (ha van ilyen)
    window.addEventListener('cart.updatedCart', () => {
      syncExtraProductDebounced();
    });
  }

  // Exportálás globális eléréshez
  window.TS_DRS = {
    init: init,
    sync: syncExtraProductDebounced,
    setConfig: (config) => {
      const hadConfig = !!(CONFIG.extraProductVariantId || CONFIG.extraProductId);
      Object.assign(CONFIG, config);

      // Töröljük a cache-t, ha változott a konfiguráció
      cachedExtraProductVariantId = null;

      // Ha most lett beállítva a konfiguráció, inicializáljuk
      if (!hadConfig && (CONFIG.extraProductVariantId || CONFIG.extraProductId)) {
        init();
      }
    },
    getConfig: () => ({ ...CONFIG })
  };

  // Automatikus inicializálás csak ha van konfiguráció
  if (CONFIG.extraProductVariantId || CONFIG.extraProductId) {
    init();
  }
})();
